import axios, { AxiosError } from 'axios';
import { indexedDBService } from './indexedDBService';
import { GlassType, Estimate, CompanyInfo } from '@/context/GlassContext';

type StoreKey = 'glassTypes' | 'estimates' | 'companyInfo';
type StoreValue<T extends StoreKey> = T extends 'glassTypes' ? GlassType[] : T extends 'estimates' ? Estimate[] : CompanyInfo;

// Define the interface for the Electron API with strict types
interface ElectronAPI {
  getAppDataPath: () => Promise<string>;
  loadData: <T>(filename: string) => Promise<{ success: boolean; data?: T; error?: string }>;
  saveData: <T>(filename: string, data: T) => Promise<{ success: boolean; error?: string }>;
  savePDF: (pdfData: string, defaultFilename: string) => Promise<{ success: boolean; filePath?: string; error?: string }>;
}

interface CustomWindow extends Window {
  electronAPI?: ElectronAPI;
}

const electronAPI = (window as CustomWindow).electronAPI;
const isElectron = !!electronAPI;

// Get API URL from environment or use default
const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001/api';

// Maximum number of retries for operations
const MAX_RETRIES = 3;
const RETRY_DELAY = 1000; // milliseconds

class StorageError extends Error {
  constructor(message: string, public readonly cause?: unknown) {
    super(message);
    this.name = 'StorageError';
  }
}

const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

const retryOperation = async <T>(
  operation: () => Promise<T>,
  retries = MAX_RETRIES,
  currentDelay = RETRY_DELAY
): Promise<T> => {
  try {
    return await operation();
  } catch (error) {
    if (retries === 0) throw error;
    await delay(currentDelay);
    return retryOperation(operation, retries - 1, currentDelay * 2);
  }
};

export const storageService = {
  async getData<T extends StoreKey>(key: T, defaultValue: StoreValue<T>): Promise<StoreValue<T>> {
    try {
      if (isElectron) {
        const result = await retryOperation(() =>
          electronAPI!.loadData<StoreValue<T>>(`${key}.json`)
        );
        
        if (result.success && result.data) {
          return result.data;
        }
        return defaultValue;
      } else {
        return await retryOperation(() =>
          indexedDBService.getData(key, defaultValue)
        );
      }
    } catch (error) {
      throw new StorageError(`Failed to load ${key}`, error);
    }
  },

  async setData<T extends StoreKey>(key: T, data: StoreValue<T>): Promise<void> {
    try {
      if (isElectron) {
        const result = await retryOperation(() =>
          electronAPI!.saveData(`${key}.json`, data)
        );
        
        if (!result.success) {
          throw new Error(result.error || 'Unknown error');
        }
      } else {
        await retryOperation(() =>
          indexedDBService.setData(key, data)
        );
      }
    } catch (error) {
      throw new StorageError(`Failed to save ${key}`, error);
    }
  },

  async savePDF(pdfData: string, filename: string): Promise<string | null> {
    try {
      if (isElectron) {
        const result = await retryOperation(() =>
          electronAPI!.savePDF(pdfData, filename)
        );
        
        if (result.success && result.filePath) {
          return result.filePath;
        }
        throw new Error(result.error || 'Failed to save PDF');
      } else {
        return await retryOperation(() =>
          indexedDBService.savePDF(pdfData, filename)
        );
      }
    } catch (error) {
      throw new StorageError('Failed to save PDF', error);
    }
  }
};
